import {
  T,
  always,
  any,
  complement,
  cond,
  equals,
  filter,
  ifElse,
  includes,
  isNil,
  juxt,
  map,
  path,
  pipe,
  propEq,
  when,
} from "ramda";
import { sendDTMEvent } from "./AdobeAnalytics";

const eventTypes = {
  AGENT_UTTERANCE: "AGENT_UTTERANCE",
  AJAX_ACTION: "AJAX_ACTION",
  BOT_UTTERANCE: "BOT_UTTERANCE",
  BOT_UTTERANCE_FEEDBACK: "BOT_UTTERANCE_FEEDBACK",
  DEVICE_INFO: "DEVICE_INFO",
  CONNECTION_EVENT: "CONNECTION_EVENT",
  ERROR: "ERROR",
  EXTERNAL_SEARCH: "EXTERNAL_SEARCH",
  LIST_VIEW_CLICK: "LIST_VIEW_CLICK",
  MAP_CLICK: "MAP_CLICK",
  MATCHED_WORD_REVIEW: "MATCHED_WORD_REVIEW",
  MATCHED_WORD_REVIEW_CLICK: "MATCHED_WORD_REVIEW_CLICK",
  MINIMIZE_WIDGET: "MINIMIZE_WIDGET",
  MISUNDERSTANDING: "MISUNDERSTANDING",
  MOUSE_ENTER: "MOUSE_ENTER",
  MOUSE_LEAVE: "MOUSE_LEAVE",
  PHONE_TAG_CLICK: "PHONE_TAG_CLICK",
  POWERED_BY_HYRO_CLICK: "POWERED_BY_HYRO_CLICK",
  PROMPT_CLOSED: "PROMPT_CLOSED",
  PROMPT_LOADED: "PROMPT_LOADED",
  PROMPT_SUGGESTION_CLICK: "PROMPT_SUGGESTION_CLICK",
  READ_RESULT: "READ_RESULT",
  RESULT_CLICK: "RESULT_CLICK",
  START_RECORDING: "START_RECORDING",
  SUGGESTION_CLICK: "SUGGESTION_CLICK",
  SWITCH_COMMAND: "SWITCH_COMMAND",
  THINKING: "THINKING",
  URL_TAG_CLICK: "URL_TAG_CLICK",
  USER_UTTERANCE: "USER_UTTERANCE",
  WIDGET_CLICK: "WIDGET_CLICK",
  WIDGET_LOAD: "WIDGET_LOAD",
};

const GA_EVENTS_TO_IGNORE = [
  eventTypes.MOUSE_ENTER,
  eventTypes.MOUSE_LEAVE,
  eventTypes.BOT_UTTERANCE_FEEDBACK,
];

const extractEventLabel = cond([
  [
    propEq("type", eventTypes.SUGGESTION_CLICK),
    ifElse(
      pipe(path(["data", "sayText"]), complement(isNil)),
      path(["data", "sayText"]),
      path(["data", "displayText"])
    ),
  ],
  [propEq("type", eventTypes.RESULT_CLICK), path(["data", "title"])],
  [propEq("type", eventTypes.WIDGET_CLICK), path(["data", "name"])],
  [propEq("type", eventTypes.DEVICE_INFO), path(["data", "osName"])],
  [propEq("type", eventTypes.USER_UTTERANCE), path(["data", "utterance"])],
  [T, always("")],
]);

const extractEventsFromAction = (sendAnalyticsEvent, events) =>
  when(
    any(propEq("type", "FLOW_DATA")),
    pipe(
      filter(propEq("type", "FLOW_DATA")),
      map(
        pipe(
          (event) => ({
            type: event.data.key.toUpperCase(),
            data: event.data.value.nodeId || event.data.value,
          }),
          sendAnalyticsEvent
        )
      )
    )
  )(events);

const sendGAEvent = (isEnabled) => ({ type, data }) => {
  if (includes(type, GA_EVENTS_TO_IGNORE) || !isEnabled) {
    return;
  }

  const eventLabel =
    typeof data === "string" ? data : extractEventLabel({ type, data });
  if (window.dataLayer) {
    window.dataLayer.push({
      event: type,
      event_category: "hyro",
      event_label: eventLabel,
      non_interaction: !isInteractiveEvent(type),
    });
  } else if (window.ga) {
    // Find all ga trackers.
    window.ga(() => {
      const trackers = window.ga.getAll();
      if (trackers && trackers.length) {
        window.ga(
          `${trackers[0].get("name")}.send`, // Sending the event to the FIRST tracker found.
          "event",
          "hyro",
          type,
          eventLabel,
          {
            nonInteraction: !isInteractiveEvent(type),
          }
        );
      }
    });
  }
};

const sendEvent = (sendSocketEvent, sendGAEvent, sendDTMEvent) => (
  type,
  data
) => {
  sendSocketEvent(type, data);
  when(
    always(equals(process.env.REACT_APP_ENV, "production")),
    juxt([sendGAEvent, sendDTMEvent])
  )({ type, data });
};

export default ({ sendSocketEvent, sendGAEvents, sendDTMEvents }) => ({
  sendEvent: sendEvent(
    sendSocketEvent,
    sendGAEvent(sendGAEvents),
    sendDTMEvent(sendDTMEvents)
  ),
  sendAnalyticsEvent: juxt([
    sendDTMEvent(sendDTMEvents),
    when(
      always(equals(process.env.REACT_APP_ENV, "production")),
      sendGAEvent(sendGAEvents)
    ),
  ]),
});

const isInteractiveEvent = (type) =>
  [
    eventTypes.SUGGESTION_CLICK,
    eventTypes.RESULT_CLICK,
    eventTypes.PHONE_TAG_CLICK,
    eventTypes.URL_TAG_CLICK,
    eventTypes.AJAX_ACTION,
    eventTypes.WIDGET_CLICK,
  ].includes(type);

export { eventTypes, extractEventsFromAction };
